import { effect, inject, Injectable } from '@angular/core';
import { IqEventBusBrokerService, IqEventBusNode } from '@dat/iq-event-bus';
import { ComponentStore } from '@ngrx/component-store';
import { omit } from 'lodash';

export type TemplateDataEvent = {
    id: string;
    template: string;
    data: any;
    eventName: string;
};

export type TemplateErrorEvent = {
    id: string;
    template: string;
    data: any;
    eventName: string;
    error: any;
};

export type EventTraceNode = {
    id: string;
    parentId: string;
    meta?: any;
};

export type AuditStore = {
    prerenderedActions: Record<string, TemplateDataEvent>;
    renderedActions: Record<string, Partial<IqEventBusNode>>;
    renderedActionsError: Record<string, TemplateErrorEvent>;
    filterActionsError: Record<string, TemplateErrorEvent>;
    eventTrace: Record<string, EventTraceNode>;
};

@Injectable({
    providedIn: 'root',
})
export class IqBuilderAuditToolsService extends ComponentStore<AuditStore> {
    private _deregister: () => void;

    private readonly _eventBroker: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );
    constructor() {
        super({
            prerenderedActions: {},
            renderedActions: {},
            renderedActionsError: {},
            filterActionsError: {},
            eventTrace: {},
        });

        this._eventBroker.watchHandledEvents((node: IqEventBusNode) => {
            setTimeout(() => {
                this.addRenderedAction(node);
                this._setEventTrace(node);
            });
        });
    }

    //#region Utils

    private _setEventTrace(node: IqEventBusNode) {
        if (!node.evt.parentId?.length) {
            return;
        }

        this.addEventTrace({
            id: node.id,
            parentId: node.evt.parentId,
            meta: node.evt.meta ?? {},
        });
    }

    //#region Actions Handlers

    //#region Source Rules

    addEventTrace = this.updater((state, node: EventTraceNode) => {
        return {
            ...state,
            eventTrace: {
                ...state.eventTrace,
                [node.id]: node,
            },
        };
    });

    readonly eventTrace = this.selectSignal((state) => state.eventTrace);
    readonly currentEventTrace = (id: string) =>
        this.selectSignal(
            this.eventTrace,
            (eventTrace) => eventTrace[id] ?? null,
        );

    readonly eventChain = (id: string) => {
        let retries = 0;
        return this.selectSignal(
            this.eventTrace,
            (eventTrace): EventTraceNode[] => {
                const eventChain: EventTraceNode[] = [];
                let currentId = id;
                while (eventTrace[currentId]) {
                    eventChain.push(eventTrace[currentId]);
                    currentId = eventTrace[currentId].parentId;
                }

                return eventChain;
            },
        );
    };

    addFilterActionsError = this.updater(
        (state, action: TemplateErrorEvent) => {
            return {
                ...state,
                filterActionsError: {
                    ...state.filterActionsError,
                    [action.id]: action,
                },
            };
        },
    );

    readonly filterActionsError = this.selectSignal(
        (state) => state.filterActionsError,
    );

    readonly currentFilterActionsError = (id: string) =>
        this.selectSignal(
            this.filterActionsError,
            (filterActionsError) => filterActionsError[id] ?? null,
        );

    //#endregion

    //#region Prerendered Actions

    addPrerenderedAction = this.updater(
        (
            state,
            action: {
                id: string;
                template: string;
                data: any;
                eventName: string;
            },
        ) => {
            return {
                ...state,
                prerenderedActions: {
                    ...state.prerenderedActions,
                    [action.id]: action,
                },
            };
        },
    );

    readonly prerenderedActions = this.selectSignal(
        (state) => state.prerenderedActions ?? {},
    );

    readonly prerenderedAction = (id: string) =>
        this.selectSignal(
            this.prerenderedActions,
            (prerenderedActions) => prerenderedActions[id] ?? null,
        );

    //#endregion

    //#region Rendered Actions
    addRenderedAction = this.updater((state, action: IqEventBusNode) => {
        return {
            ...state,
            renderedActions: {
                ...state.renderedActions,
                [action.id]: { ...omit(action, ['nextEvent']) },
            },
        };
    });

    readonly renderedActions = this.selectSignal(
        (state) => state.renderedActions,
    );

    readonly renderedAction = (id: string) =>
        this.selectSignal(
            this.renderedActions,
            (renderedActions) => renderedActions[id] ?? null,
        );

    addRenderedActionError = this.updater(
        (state, action: TemplateErrorEvent) => {
            return {
                ...state,
                renderedActionsError: {
                    ...state.renderedActionsError,
                    [action.id]: action,
                },
            };
        },
    );

    readonly renderedActionsError = this.selectSignal(
        (state) => state.renderedActionsError,
    );

    readonly currentRenderedActionError = (id: string) =>
        this.selectSignal(
            this.renderedActionsError,
            (renderedActionsError) => renderedActionsError[id] ?? null,
        );

    //#endregion

    //#endregion
}
